import type { ReactNode } from 'react';
import { Button } from '@/components/base/Button';
import { t } from '@/messages/zh-TW';
import { ModalFrame } from '../ModalFrame';

/**
 * 共用對話框 ③：成功訊息。
 *
 * 原系統函式：`showInfoDialog(content, callbackFunction, buttons)`，`js-my/common.js:317-338`
 *   `:318-322` 預設按鈕組 `myButtons = { '確認': cb }`；傳 `buttons` 可整組覆寫（`:324-326`）
 *   `:330` `icon: 'fa fa-info-circle'`
 *   `:331` `title: '訊息'`
 *   `:333` `type: 'blue'`
 *   `:335` `closeIcon: true`
 * 呼叫範例：`common.js:1108` `common.showInfoDialog(response.desc)`、
 *           `layout/navbar.jsp:55` `showInfoDialog('設定成功!')`。
 *
 * ※ `zh-TW.ts` 的 `dialog.infoTitle` 值是「完成」，與原系統的「訊息」不同；
 *   本元件用 `dialog.infoTitle`（值已依原始碼校正為「訊息」）。
 *   TODO(messages): 由 messages 負責人裁定 `dialog.infoTitle` 是否改成「訊息」。
 *
 * `AUDIT_GATE3_HANDOFF.md` §1.1：這個對話框在全部線稿中未入稿。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface InfoDialogProps {
  uiId: string;
  open: boolean;
  message: ReactNode;
  /** 覆寫標題，預設「訊息」（common.js:331）。 */
  title?: string;
  onClose(): void;
  /**
   * 覆寫按鈕列（對應 common.js:324-326 的 `buttons` 參數）。
   * ponytail: 只開一個 ReactNode 缺口，不做「按鈕描述物件陣列」的小型 DSL ——
   * 呼叫端直接放 <Button> 更好讀，也不用維護一套自製 schema。
   */
  footer?: ReactNode;
}

export function InfoDialog({ uiId, open, message, title, onClose, footer }: InfoDialogProps) {
  return (
    <ModalFrame
      uiId={uiId}
      open={open}
      title={title ?? t('dialog.infoTitle')}
      tone="info"
      dismissible
      onClose={onClose}
      footer={
        footer ?? (
          <Button uiId={`${uiId}-ok`} variant="primary" size="sm" type="button" onClick={onClose}>
            {t('action.acknowledge')}
          </Button>
        )
      }
    >
      <div data-ui-id={`${uiId}-message`}>{message}</div>
    </ModalFrame>
  );
}
